import { useRef, useState, useEffect, useContext, useLayoutEffect } from "react";
import { CommandBarButton, IconButton, Dialog, DialogType, Stack } from "@fluentui/react";
import { SquareRegular, ShieldLockRegular, ErrorCircleRegular, ClipboardCheckmarkRegular } from "@fluentui/react-icons";

import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm'
import rehypeRaw from "rehype-raw";
import uuid from 'react-uuid';
import { isEmpty } from "lodash-es";
import DOMPurify from 'dompurify';

import styles from "./Chat.module.css";
import Contoso from "../../assets/Contoso.svg";
import { XSSAllowTags } from "../../constants/xssAllowTags";

import { prettyPrintJson } from 'pretty-print-json';

import { useParams } from "react-router-dom";

import {
    ChatMessage,
    ConversationRequest,
    conversationApi,
    audioConversationApi,
    Citation,
    ToolMessageContent,
    ChatResponse,
    getUserInfo,
    Conversation,
    historyGenerate,
    historyUpdate,
    historyClear,
    ChatHistoryLoadingState,
    CosmosDBStatus,
    ErrorMessage,
    APIConfig,
    autoQMApi,
    Answers,
    getAutoQMforIdApi,
    frontendSettings,
    FrontendSettings,
    apiConfigSettings
} from "../../api";
import { Answer } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ChatHistoryPanel } from "../../components/ChatHistory/ChatHistoryPanel";
import { AppStateContext } from "../../state/AppProvider";
import { useBoolean } from "@fluentui/react-hooks";
import AudioRecorder from "../../components/AudioRecorder/components/AudioRecordingComponent";

const enum messageStatus {
    NotRunning = "Not Running",
    Processing = "Processing",
    Done = "Done"
}

const Chat = () => {
    const appStateContext = useContext(AppStateContext)
    const ui = appStateContext?.state.frontendSettings?.ui;
    const AUTH_ENABLED = appStateContext?.state.frontendSettings?.auth_enabled;
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
    const [activeCitation, setActiveCitation] = useState<Citation>();
    const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);
    const abortFuncs = useRef([] as AbortController[]);
    const [showAuthMessage, setShowAuthMessage] = useState<boolean>(true);
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning);
    const [clearingChat, setClearingChat] = useState<boolean>(false);
    const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true);
    const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>()
    const isPlaying = useRef<boolean>(false);
    const params = new URLSearchParams(window.location.search);
    const typename = params.get("typename");
    const id = params.get("id");
    const etn = params.get("typename");

    const errorDialogContentProps = {
        type: DialogType.close,
        title: errorMsg?.title,
        closeButtonAriaLabel: 'Close',
        subText: errorMsg?.subtitle,
    };

    const modalProps = {
        titleAriaId: 'labelId',
        subtitleAriaId: 'subTextId',
        isBlocking: true,
        styles: { main: { maxWidth: 450 } },
    }

    const [ASSISTANT, TOOL, ERROR] = ["assistant", "tool", "error"]
    const NO_CONTENT_ERROR = "No content in messages object."

    useEffect(() => {
        if (appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.Working
            && appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured
            && appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail
            && hideErrorDialog) {
            let subtitle = `${appStateContext.state.isCosmosDBAvailable.status}. Please contact the site administrator.`
            setErrorMsg({
                title: "Chat history is not enabled",
                subtitle: subtitle
            })
            toggleErrorDialog();
        }
    }, [appStateContext?.state.isCosmosDBAvailable]);

    const handleErrorDialogClose = () => {
        toggleErrorDialog()
        setTimeout(() => {
            setErrorMsg(null)
        }, 500);
    }

    const hasExecuted = useRef(false); // Track if the effect has already run
    useEffect(() => {
        if (!hasExecuted.current && id && etn) {
            hasExecuted.current = true; // Mark as executed
            //setIsAQMLoading(true);

            const getFrontendSettings = async () => {
                apiConfigSettings()
                    .then((response) => {
                        if (response) {
                            response.json().then((payload) => {
                                if (appStateContext) {
                                    appStateContext.dispatch({
                                        type: 'UPDATE_CONFIG_SETTINGS',
                                        payload: payload as APIConfig,
                                    });
                                }
                            });
                        }
                    })
                    .catch((err) => {
                        console.error("There was an issue fetching your data: " + err);
                    });
            };

            getFrontendSettings().then(() => {
                loadAutoQMforId(
                    appStateContext?.state.currentChat?.id
                        ? appStateContext?.state.currentChat?.id
                        : undefined,
                    id, etn
                );
            });

            console.log("Loading AutoQM for ID: " + id);
        }
    }, [id, etn, appStateContext]);

    useEffect(() => {
        setIsLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading)
    }, [appStateContext?.state.chatHistoryLoadingState])

    const getUserInfoList = async () => {
        if (!AUTH_ENABLED) {
            setShowAuthMessage(false);
            return;
        }
        const userInfoList = await getUserInfo();
        if (userInfoList.length === 0 && window.location.hostname !== "127.0.0.1") {
            setShowAuthMessage(true);
        }
        else {
            setShowAuthMessage(false);
        }
    }

    let assistantMessage = {} as ChatMessage
    let toolMessage = {} as ChatMessage
    let assistantContent = ""

    const processResultMessage = (resultMessage: ChatMessage, userMessage: ChatMessage, conversationId?: string) => {
        if (resultMessage.role === ASSISTANT) {
            assistantContent += resultMessage.content
            assistantMessage = resultMessage
            assistantMessage.content = assistantContent

            if (resultMessage.context) {
                toolMessage = {
                    id: uuid(),
                    role: TOOL,
                    content: resultMessage.context,
                    date: new Date().toISOString(),
                }
            }
        }

        if (resultMessage.role === TOOL) toolMessage = resultMessage

        if (!conversationId) {
            isEmpty(toolMessage) ?
                setMessages([...messages, userMessage, assistantMessage]) :
                setMessages([...messages, userMessage, toolMessage, assistantMessage]);
        } else {
            isEmpty(toolMessage) ?
                setMessages([...messages, assistantMessage]) :
                setMessages([...messages, toolMessage, assistantMessage]);
        }
    }

    const loadAutoQMforId = async (conversationId?: string, recordId?: string, etn?: string) => {
        setIsLoading(true);
        setShowLoadingMessage(true);
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        {

            const userMessage: ChatMessage = {
                id: uuid(),
                role: "user",
                content: "Get AutoQM for recordId: " + recordId,
                date: new Date().toISOString(),
            };

            let conversation: Conversation | null | undefined;
            if (!conversationId) {
                conversation = {
                    id: conversationId ?? uuid(),
                    title: "AutoQM",
                    messages: [userMessage],
                    date: new Date().toISOString(),

                }

            } else {
                conversation = appStateContext?.state?.currentChat
                if (!conversation) {
                    console.error("Conversation not found.");
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                    return;
                } else {
                    conversation.messages.push(userMessage);
                }
            }

            // if (appStateContext?.state.currentPhase == "") {
            //     let errorMessage = "No more content to process. Please start a new conversation.";

            //     let errorChatMsg: ChatMessage = {
            //         id: uuid(),
            //         role: ERROR,
            //         content: errorMessage,
            //         date: new Date().toISOString()
            //     }
            //     conversation.messages.push(errorChatMsg);
            //     appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
            //     setMessages([...messages, errorChatMsg]);
            //     setIsLoading(false);
            //     setShowLoadingMessage(false);
            //     abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
            //     setProcessMessages(messageStatus.Done)
            //     return;
            // }

            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
            setMessages(conversation.messages)

            const request: ConversationRequest = {
                messages: [...conversation.messages.filter((answer) => !isEmpty(answer) && answer.role !== TOOL && answer.role !== ERROR && answer.role !== "intro" && answer.type !== 'metadata')]
            };

            let result = {} as ChatResponse;
            try {
                const response = await getAutoQMforIdApi(appStateContext?.state.frontendSettings?.config as APIConfig, id as string, etn as string);
                const payload = await response.json();

                let content = payload;

                if (content != null && content != undefined && content.record != null) {
                    let title = payload.record.Title;
                    let issue = payload.record.Issue;
                    let resolution = payload.record.Resolution;
                    let recap = payload.record.Recap;
                    let narrative = payload.record.Narrative;

                    const answers: string[] = JSON.parse(payload.record.AnswerList);
                    const answerlist: Answers[] = []

                    for (let i = 0; i < answers.length; i++) {
                        const answer: Answers = {
                            question: JSON.parse(answers[i]).question,
                            answer: JSON.parse(answers[i]).answer
                        }
                        answerlist.push(answer);
                    }

                    const systemaqmMessage: ChatMessage = {
                        id: uuid(),
                        role: "assistant",
                        type: "autoqm",
                        content: JSON.stringify(payload.record, null, 2),
                        score: payload.record.Score,
                        title: title,
                        issue: issue,
                        resolution: resolution,
                        recap: recap,
                        narrative: narrative,
                        answers: answerlist,
                        date: new Date().toISOString(),
                        openaiObj: payload?.record,
                    };

                    conversation.messages.push(systemaqmMessage);
                    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                    setMessages([...messages, systemaqmMessage]);
                }

                const systemMessage: ChatMessage = {
                    id: uuid(),
                    role: "system",
                    type: "text",
                    content: "",
                    date: new Date().toISOString(),
                };

                processResultMessage(systemMessage, userMessage, conversationId);

                conversation.messages.push(toolMessage, assistantMessage)
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages([...messages, toolMessage, assistantMessage]);

                appStateContext?.dispatch({ type: 'SET_CURRENT_PHASE', payload: payload.nextStepName });
                appStateContext?.dispatch({ type: 'SET_RAW_CUSTOMER_DATA', payload: payload });

                if (payload.openaiObj != null && appStateContext?.state.frontendSettings?.config?.showmetadata) {
                    const metaMessage: ChatMessage = {
                        id: uuid(),
                        role: "assistant",
                        type: "metadata",
                        content: JSON.stringify(payload, null, 2),
                        date: new Date().toISOString(),
                    };

                    processResultMessage(metaMessage, userMessage, conversationId);

                    conversation.messages.push(toolMessage, assistantMessage)
                    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                    setMessages([...messages, toolMessage, assistantMessage]);
                }


            } catch (e) {
                if (!abortController.signal.aborted) {
                    let errorMessage = "An error occurred. Please try again. If the problem persists, please contact the site administrator. " + e;
                    if (result.error?.message) {
                        errorMessage = result.error.message;
                    }
                    else if (typeof result.error === "string") {
                        errorMessage = result.error;
                    }
                    let errorChatMsg: ChatMessage = {
                        id: uuid(),
                        role: ERROR,
                        content: errorMessage,
                        date: new Date().toISOString()
                    }
                    conversation.messages.push(errorChatMsg);
                    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                    setMessages([...messages, errorChatMsg]);
                } else {
                    setMessages([...messages, userMessage])
                }
            } finally {
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                setProcessMessages(messageStatus.Done)
            }
        }

        return abortController.abort();

    }

    const autoQM = async (conversationId?: string) => {
        setIsLoading(true);
        setShowLoadingMessage(true);
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        if (appStateContext?.state.currentPhase == "initial") {

            const userMessage: ChatMessage = {
                id: uuid(),
                role: "user",
                content: "Get Transcription and Metadata Results",
                date: new Date().toISOString(),
            };

            const systemMessage: ChatMessage = {
                id: uuid(),
                role: "assistant",
                type: "text",
                content: "Call Transcription:\n\n```\n" + appStateContext.state.frontendSettings?.config?.customerdata + "\n```\n\nMetadata:\n\n```json\n" + JSON.stringify(appStateContext.state.frontendSettings?.config?.rawcustomerdata, null, 2),
                date: new Date().toISOString(),
            };

            let conversation: Conversation | null | undefined;
            if (!conversationId) {
                conversation = {
                    id: conversationId ?? uuid(),
                    title: "AutoQM",
                    messages: [userMessage, systemMessage],
                    date: new Date().toISOString(),

                }

            } else {
                conversation = appStateContext?.state?.currentChat
                if (!conversation) {
                    console.error("Conversation not found.");
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                    return;
                } else {
                    conversation.messages.push(userMessage);
                }
            }

            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
            setMessages(conversation.messages)
            appStateContext?.dispatch({ type: 'SET_CURRENT_PHASE', payload: JSON.parse(JSON.stringify(appStateContext.state.frontendSettings?.config?.rawcustomerdata)).nextStepName.toString() });

            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
            setProcessMessages(messageStatus.Done)
        }
        else {

            const userMessage: ChatMessage = {
                id: uuid(),
                role: "user",
                content: "Get content for: " + appStateContext?.state.currentPhase,
                date: new Date().toISOString(),
            };

            let conversation: Conversation | null | undefined;
            if (!conversationId) {
                conversation = {
                    id: conversationId ?? uuid(),
                    title: "AutoQM",
                    messages: [userMessage],
                    date: new Date().toISOString(),

                }

            } else {
                conversation = appStateContext?.state?.currentChat
                if (!conversation) {
                    console.error("Conversation not found.");
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                    return;
                } else {
                    conversation.messages.push(userMessage);
                }
            }

            if (appStateContext?.state.currentPhase == "") {
                let errorMessage = "No more content to process. Please start a new conversation.";

                let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: errorMessage,
                    date: new Date().toISOString()
                }
                conversation.messages.push(errorChatMsg);
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages([...messages, errorChatMsg]);
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                setProcessMessages(messageStatus.Done)
                return;
            }

            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
            setMessages(conversation.messages)

            const request: ConversationRequest = {
                messages: [...conversation.messages.filter((answer) => !isEmpty(answer) && answer.role !== TOOL && answer.role !== ERROR && answer.role !== "intro" && answer.type !== 'metadata')]
            };

            let result = {} as ChatResponse;
            try {
                const response = await autoQMApi(appStateContext?.state.frontendSettings?.config as APIConfig);
                const payload = await response.json();

                let content = payload;

                if (appStateContext?.state.currentPhase == "autoqmenrichments") {
                    let title = payload.record.Title;
                    let issue = payload.record.Issue;
                    let resolution = payload.record.Resolution;
                    let recap = payload.record.Recap;
                    let narrative = payload.record.Narrative;

                    const answers: string[] = JSON.parse(payload.record.AnswerList);
                    const answerlist: Answers[] = []

                    for (let i = 0; i < answers.length; i++) {
                        const answer: Answers = {
                            question: JSON.parse(answers[i]).question,
                            answer: JSON.parse(answers[i]).answer
                        }
                        answerlist.push(answer);
                    }

                    const systemaqmMessage: ChatMessage = {
                        id: uuid(),
                        role: "assistant",
                        type: "autoqm",
                        content: JSON.stringify(payload.record, null, 2),
                        score: payload.record.Score,
                        title: title,
                        issue: issue,
                        resolution: resolution,
                        recap: recap,
                        narrative: narrative,
                        answers: answerlist,
                        date: new Date().toISOString(),
                        openaiObj: payload?.record,
                    };

                    conversation.messages.push(systemaqmMessage);
                    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                    setMessages([...messages, systemaqmMessage]);
                }

                const systemMessage: ChatMessage = {
                    id: uuid(),
                    role: "assistant",
                    type: "text",
                    content: "Call Enriched Metadata:\n\n```json\n" + JSON.stringify(payload, null, 2),
                    date: new Date().toISOString(),
                };

                processResultMessage(systemMessage, userMessage, conversationId);

                conversation.messages.push(toolMessage, assistantMessage)
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages([...messages, toolMessage, assistantMessage]);

                appStateContext?.dispatch({ type: 'SET_CURRENT_PHASE', payload: payload.nextStepName });
                appStateContext?.dispatch({ type: 'SET_RAW_CUSTOMER_DATA', payload: payload });

                if (payload.openaiObj != null && appStateContext?.state.frontendSettings?.config?.showmetadata) {
                    const metaMessage: ChatMessage = {
                        id: uuid(),
                        role: "assistant",
                        type: "metadata",
                        content: JSON.stringify(payload, null, 2),
                        date: new Date().toISOString(),
                    };

                    processResultMessage(metaMessage, userMessage, conversationId);

                    conversation.messages.push(toolMessage, assistantMessage)
                    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                    setMessages([...messages, toolMessage, assistantMessage]);
                }


            } catch (e) {
                if (!abortController.signal.aborted) {
                    let errorMessage = "An error occurred. Please try again. If the problem persists, please contact the site administrator. " + e;
                    if (result.error?.message) {
                        errorMessage = result.error.message;
                    }
                    else if (typeof result.error === "string") {
                        errorMessage = result.error;
                    }
                    let errorChatMsg: ChatMessage = {
                        id: uuid(),
                        role: ERROR,
                        content: errorMessage,
                        date: new Date().toISOString()
                    }
                    conversation.messages.push(errorChatMsg);
                    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                    setMessages([...messages, errorChatMsg]);
                } else {
                    setMessages([...messages, userMessage])
                }
            } finally {
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                setProcessMessages(messageStatus.Done)
            }
        }

        return abortController.abort();
    }

    const addAudioElement = async (blob: Blob, conversationId?: string) => {
        setIsLoading(true);
        setShowLoadingMessage(true);
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        const url = URL.createObjectURL(blob);
        const audio = document.createElement("audio");
        audio.src = url;
        audio.controls = true;


        const userMessage: ChatMessage = {
            id: uuid(),
            role: "user",
            type: "audio",
            content: "",
            audio: audio,
            blob: blob,
            date: new Date().toISOString(),
        };

        const transcribedQuestion: string = "";
        let conversation: Conversation | null | undefined;
        if (!conversationId) {
            conversation = {
                id: conversationId ?? uuid(),
                title: transcribedQuestion,
                messages: [userMessage],
                date: new Date().toISOString(),
            }

            // const intromaudio = document.createElement("audio");
            // intromaudio.src = appStateContext?.state.frontendSettings?.config?.intromessage as string;
            // intromaudio.controls = true;

            const intro = appStateContext?.state.frontendSettings?.config?.intromessage as string;
            if (intro.length > 0) {
                const introMessage: ChatMessage = {
                    id: uuid(),
                    role: "intro",
                    type: "text",
                    content: intro,
                    date: new Date().toISOString(),
                };
                conversation.messages.push(introMessage);
            }


        } else {
            conversation = appStateContext?.state?.currentChat
            if (!conversation) {
                console.error("Conversation not found.");
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                return;
            } else {
                conversation.messages.push(userMessage);

                const intro = appStateContext?.state.frontendSettings?.config?.respondingmessage as string;
                if (intro.length > 0) {
                    const introMessage: ChatMessage = {
                        id: uuid(),
                        role: "intro",
                        type: "text",
                        content: appStateContext?.state.frontendSettings?.config?.respondingmessage as string,
                        date: new Date().toISOString(),
                    };

                    conversation.messages.push(introMessage);
                }
            }
        }

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
        setMessages(conversation.messages)

        const request: ConversationRequest = {
            messages: [...conversation.messages.filter((answer) => !isEmpty(answer) && answer.role !== TOOL && answer.role !== ERROR && answer.role !== "intro" && answer.type !== 'metadata')]
        };

        let result = {} as ChatResponse;
        try {
            const response = await audioConversationApi(request, appStateContext?.state.frontendSettings?.config as APIConfig);
            const payload = await response.json();

            const systemaudio = document.createElement("audio");
            systemaudio.src = payload.audio;
            systemaudio.controls = true;

            let content = "";
            if (payload?.openaiResults?.answerFound) {
                if (payload?.openaiResults?.jsonFound) content = payload?.openaiObj?.answer;
                else {
                    const index = payload?.openaiObj?.indexOf("answer: ");
                    content = payload?.openaiObj?.substring(index + 8);
                }
            }
            else {
                content = payload?.openaiObj;
            }

            const systemMessage: ChatMessage = {
                id: uuid(),
                role: "assistant",
                type: appStateContext?.state?.frontendSettings?.config?.replymode !== "text" ? "audio" : "text",
                content: content,
                audio: systemaudio,
                date: new Date().toISOString(),
                openaiObj: payload?.openaiObj,
            };

            processResultMessage(systemMessage, userMessage, conversationId);

            conversation.messages.push(toolMessage, assistantMessage)
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
            setMessages([...messages, toolMessage, assistantMessage]);

            if (payload.openaiObj != null && appStateContext?.state.frontendSettings?.config?.showmetadata) {
                const metaMessage: ChatMessage = {
                    id: uuid(),
                    role: "assistant",
                    type: "metadata",
                    content: JSON.stringify(payload, null, 2),
                    date: new Date().toISOString(),
                };

                processResultMessage(metaMessage, userMessage, conversationId);

                conversation.messages.push(toolMessage, assistantMessage)
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages([...messages, toolMessage, assistantMessage]);
            }

        } catch (e) {
            if (!abortController.signal.aborted) {
                let errorMessage = "An error occurred. Please try again. If the problem persists, please contact the site administrator. " + e;
                if (result.error?.message) {
                    errorMessage = result.error.message;
                }
                else if (typeof result.error === "string") {
                    errorMessage = result.error;
                }
                let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: errorMessage,
                    date: new Date().toISOString()
                }
                conversation.messages.push(errorChatMsg);
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages([...messages, errorChatMsg]);
            } else {
                setMessages([...messages, userMessage])
            }
        } finally {
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
            setProcessMessages(messageStatus.Done)
        }

        return abortController.abort();

    };

    async function delay(ms: number) {

        console.log("Is Playing: " + isPlaying.current);
        while (isPlaying.current) {
            const delayMs = 1000; // 1 second
            setTimeout(() => {
                // Your code to execute after the delay
                console.log("1 second has passed!");
            }, delayMs);
        }
        //return new Promise(resolve => {setTimeout(resolve, ms)});
        return;
    }

    const makeApiRequestWithoutCosmosDB = async (question: string, conversationId?: string) => {
        console.log("API Config Settings from context: ", appStateContext?.state.frontendSettings?.config)
        setIsLoading(true);
        setShowLoadingMessage(true);
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        const userMessage: ChatMessage = {
            id: uuid(),
            role: "user",
            content: question,
            date: new Date().toISOString(),
        };

        let conversation: Conversation | null | undefined;
        if (!conversationId) {
            conversation = {
                id: conversationId ?? uuid(),
                title: question,
                messages: [userMessage],
                date: new Date().toISOString(),

            }

            // const intromaudio = document.createElement("audio");
            // intromaudio.src = appStateContext?.state.frontendSettings?.config?.intromessage as string;
            // intromaudio.controls = true;
            console.log("1");
            const intro = appStateContext?.state.frontendSettings?.config?.intromessage as string;
            if (intro.length > 0) {
                const introMessage: ChatMessage = {
                    id: uuid(),
                    role: "intro",
                    type: "text",
                    content: intro,
                    date: new Date().toISOString(),
                };
                conversation.messages.push(introMessage);
            }

        } else {
            conversation = appStateContext?.state?.currentChat
            if (!conversation) {
                console.error("Conversation not found.");
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                return;
            } else {
                conversation.messages.push(userMessage);

                console.log("2");
                const intro = appStateContext?.state.frontendSettings?.config?.respondingmessage as string;
                if (intro.length > 0) {
                    const introMessage: ChatMessage = {
                        id: uuid(),
                        role: "intro",
                        type: "text",
                        content: appStateContext?.state.frontendSettings?.config?.respondingmessage as string,
                        date: new Date().toISOString(),
                    };

                    conversation.messages.push(introMessage);
                }
            }
        }

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
        setMessages(conversation.messages)

        const request: ConversationRequest = {
            messages: [...conversation.messages.filter((answer) => !isEmpty(answer) && answer.role !== TOOL && answer.role !== ERROR && answer.role !== "intro" && answer.type !== 'metadata')]
        };

        console.log("3");
        let result = {} as ChatResponse;
        try {
            const response = await conversationApi(request, appStateContext?.state.frontendSettings?.config as APIConfig);
            const payload = await response.json();


            const systemaudio = document.createElement("audio");
            systemaudio.src = payload.audio;
            systemaudio.controls = true;

            let content = "";
            if (payload?.openaiResults?.answerFound) {
                if (payload?.openaiResults?.jsonFound) content = payload?.openaiObj?.answer;
                else {
                    const index = payload?.openaiObj?.indexOf("answer: ");
                    content = payload?.openaiObj?.substring(index + 8);
                }
            }
            else {
                content = payload?.openaiObj;
            }

            console.log("4");
            const systemMessage: ChatMessage = {
                id: uuid(),
                role: "assistant",
                type: appStateContext?.state?.frontendSettings?.config?.replymode !== "voice" ? "text" : "audio",
                content: content,
                audio: systemaudio,
                date: new Date().toISOString(),
                openaiObj: payload?.openaiObj,
            };

            processResultMessage(systemMessage, userMessage, conversationId);

            conversation.messages.push(toolMessage, assistantMessage)
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
            setMessages([...messages, toolMessage, assistantMessage]);

            console.log("5");
            if (payload.openaiObj != null && appStateContext?.state.frontendSettings?.config?.showmetadata) {
                const metaMessage: ChatMessage = {
                    id: uuid(),
                    role: "assistant",
                    type: "metadata",
                    content: JSON.stringify(payload, null, 2),
                    date: new Date().toISOString(),
                };

                processResultMessage(metaMessage, userMessage, conversationId);

                conversation.messages.push(toolMessage, assistantMessage)
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages([...messages, toolMessage, assistantMessage]);
            }
            console.log("6");

        } catch (e) {
            if (!abortController.signal.aborted) {
                //console.log(e);
                let errorMessage = "An error occurred. Please try again. If the problem persists, please contact the site administrator. " + e;
                if (result.error?.message) {
                    errorMessage = result.error.message;
                }
                else if (typeof result.error === "string") {
                    errorMessage = result.error;
                }
                let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: errorMessage,
                    date: new Date().toISOString()
                }
                conversation.messages.push(errorChatMsg);
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages([...messages, errorChatMsg]);
            } else {
                setMessages([...messages, userMessage])
            }
        } finally {
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
            setProcessMessages(messageStatus.Done)
        }

        return abortController.abort();
    };

    const makeApiRequestWithCosmosDB = async (question: string, conversationId?: string) => {
        setIsLoading(true);
        setShowLoadingMessage(true);
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        const userMessage: ChatMessage = {
            id: uuid(),
            role: "user",
            content: question,
            date: new Date().toISOString(),
        };

        //api call params set here (generate)
        let request: ConversationRequest;
        let conversation;
        if (conversationId) {
            conversation = appStateContext?.state?.chatHistory?.find((conv) => conv.id === conversationId)
            if (!conversation) {
                console.error("Conversation not found.");
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                return;
            } else {
                conversation.messages.push(userMessage);
                request = {
                    messages: [...conversation.messages.filter((answer) => answer.role !== ERROR)]
                };
            }
        } else {
            request = {
                messages: [userMessage].filter((answer) => answer.role !== ERROR)
            };
            setMessages(request.messages)
        }
        let result = {} as ChatResponse;
        try {
            const response = conversationId ? await historyGenerate(request, abortController.signal, conversationId) : await historyGenerate(request, abortController.signal);
            if (!response?.ok) {
                const responseJson = await response.json();
                var errorResponseMessage = responseJson.error === undefined ? "Please try again. If the problem persists, please contact the site administrator." : responseJson.error;
                let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: `There was an error generating a response. Chat history can't be saved at this time. ${errorResponseMessage}`,
                    date: new Date().toISOString()
                }
                let resultConversation;
                if (conversationId) {
                    resultConversation = appStateContext?.state?.chatHistory?.find((conv) => conv.id === conversationId)
                    if (!resultConversation) {
                        console.error("Conversation not found.");
                        setIsLoading(false);
                        setShowLoadingMessage(false);
                        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                        return;
                    }
                    resultConversation.messages.push(errorChatMsg);
                } else {
                    setMessages([...messages, userMessage, errorChatMsg])
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                    return;
                }
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation });
                setMessages([...resultConversation.messages]);
                return;
            }
            if (response?.body) {
                const reader = response.body.getReader();

                let runningText = "";
                while (true) {
                    setProcessMessages(messageStatus.Processing)
                    const { done, value } = await reader.read();
                    if (done) break;

                    var text = new TextDecoder("utf-8").decode(value);
                    const objects = text.split("\n");
                    objects.forEach((obj) => {
                        try {
                            if (obj !== "" && obj !== "{}") {
                                runningText += obj;
                                result = JSON.parse(runningText);
                                if (!result.choices?.[0]?.messages?.[0].content) {
                                    errorResponseMessage = NO_CONTENT_ERROR;
                                    throw Error();
                                }
                                if (result.choices?.length > 0) {
                                    result.choices[0].messages.forEach((msg) => {
                                        msg.id = result.id;
                                        msg.date = new Date().toISOString();
                                    })
                                    if (result.choices[0].messages?.some(m => m.role === ASSISTANT)) {
                                        setShowLoadingMessage(false);
                                    }
                                    result.choices[0].messages.forEach((resultObj) => {
                                        processResultMessage(resultObj, userMessage, conversationId);
                                    })
                                }
                                runningText = "";
                            }
                            else if (result.error) {
                                throw Error(result.error);
                            }
                        }
                        catch (e) {
                            if (!(e instanceof SyntaxError)) {
                                console.error(e);
                                throw e;
                            } else {
                                console.log("Incomplete message. Continuing...")
                            }
                        }
                    });
                }

                let resultConversation;
                if (conversationId) {
                    resultConversation = appStateContext?.state?.chatHistory?.find((conv) => conv.id === conversationId)
                    if (!resultConversation) {
                        console.error("Conversation not found.");
                        setIsLoading(false);
                        setShowLoadingMessage(false);
                        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                        return;
                    }
                    isEmpty(toolMessage) ?
                        resultConversation.messages.push(assistantMessage) :
                        resultConversation.messages.push(toolMessage, assistantMessage)
                } else {
                    resultConversation = {
                        id: result.history_metadata.conversation_id,
                        title: result.history_metadata.title,
                        messages: [userMessage],
                        date: result.history_metadata.date
                    }
                    isEmpty(toolMessage) ?
                        resultConversation.messages.push(assistantMessage) :
                        resultConversation.messages.push(toolMessage, assistantMessage)
                }
                if (!resultConversation) {
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                    return;
                }
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation });
                isEmpty(toolMessage) ?
                    setMessages([...messages, assistantMessage]) :
                    setMessages([...messages, toolMessage, assistantMessage]);
            }

        } catch (e) {
            if (!abortController.signal.aborted) {
                let errorMessage = `An error occurred. ${errorResponseMessage}`;
                if (result.error?.message) {
                    errorMessage = result.error.message;
                }
                else if (typeof result.error === "string") {
                    errorMessage = result.error;
                }
                let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: errorMessage,
                    date: new Date().toISOString()
                }
                let resultConversation;
                if (conversationId) {
                    resultConversation = appStateContext?.state?.chatHistory?.find((conv) => conv.id === conversationId)
                    if (!resultConversation) {
                        console.error("Conversation not found.");
                        setIsLoading(false);
                        setShowLoadingMessage(false);
                        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                        return;
                    }
                    resultConversation.messages.push(errorChatMsg);
                } else {
                    if (!result.history_metadata) {
                        console.error("Error retrieving data.", result);
                        let errorChatMsg: ChatMessage = {
                            id: uuid(),
                            role: ERROR,
                            content: errorMessage,
                            date: new Date().toISOString()
                        }
                        setMessages([...messages, userMessage, errorChatMsg])
                        setIsLoading(false);
                        setShowLoadingMessage(false);
                        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                        return;
                    }
                    resultConversation = {
                        id: result.history_metadata.conversation_id,
                        title: result.history_metadata.title,
                        messages: [userMessage],
                        date: result.history_metadata.date
                    }
                    resultConversation.messages.push(errorChatMsg);
                }
                if (!resultConversation) {
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                    return;
                }
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation });
                setMessages([...messages, errorChatMsg]);
            } else {
                setMessages([...messages, userMessage])
            }
        } finally {
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
            setProcessMessages(messageStatus.Done)
        }
        return abortController.abort();

    }

    const clearChat = async () => {
        setClearingChat(true)
        if (appStateContext?.state.currentChat?.id && appStateContext?.state.isCosmosDBAvailable.cosmosDB) {
            let response = await historyClear(appStateContext?.state.currentChat.id)
            if (!response.ok) {
                setErrorMsg({
                    title: "Error clearing current chat",
                    subtitle: "Please try again. If the problem persists, please contact the site administrator.",
                })
                toggleErrorDialog();
            } else {
                appStateContext?.dispatch({ type: 'DELETE_CURRENT_CHAT_MESSAGES', payload: appStateContext?.state.currentChat.id });
                appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext?.state.currentChat });
                setActiveCitation(undefined);
                setIsCitationPanelOpen(false);
                setMessages([])
            }
        }
        setClearingChat(false)
    };

    const newChat = () => {
        setProcessMessages(messageStatus.Processing)
        setMessages([])
        setIsCitationPanelOpen(false);
        setActiveCitation(undefined);
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null });
        setProcessMessages(messageStatus.Done)
    };

    const stopGenerating = () => {
        abortFuncs.current.forEach(a => a.abort());
        setShowLoadingMessage(false);
        setIsLoading(false);
    }

    useEffect(() => {
        if (appStateContext?.state.currentChat) {
            setMessages(appStateContext.state.currentChat.messages)
        } else {
            setMessages([])
        }
    }, [appStateContext?.state.currentChat]);

    useLayoutEffect(() => {
        const saveToDB = async (messages: ChatMessage[], id: string) => {
            const response = await historyUpdate(messages, id)
            return response
        }

        if (appStateContext && appStateContext.state.currentChat && processMessages === messageStatus.Done) {
            if (appStateContext.state.isCosmosDBAvailable.cosmosDB) {
                if (!appStateContext?.state.currentChat?.messages) {
                    console.error("Failure fetching current chat state.")
                    return
                }
                const noContentError = appStateContext.state.currentChat.messages.find(m => m.role === ERROR)

                if (noContentError && !noContentError.content.includes(NO_CONTENT_ERROR)) {
                    saveToDB(appStateContext.state.currentChat.messages, appStateContext.state.currentChat.id)
                        .then((res) => {
                            if (!res.ok) {
                                let errorMessage = "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator.";
                                let errorChatMsg: ChatMessage = {
                                    id: uuid(),
                                    role: ERROR,
                                    content: errorMessage,
                                    date: new Date().toISOString()
                                }
                                if (!appStateContext?.state.currentChat?.messages) {
                                    let err: Error = {
                                        ...new Error,
                                        message: "Failure fetching current chat state."
                                    }
                                    throw err
                                }
                                setMessages([...appStateContext?.state.currentChat?.messages, errorChatMsg])
                            }
                            return res as Response
                        })
                        .catch((err) => {
                            console.error("Error: ", err)
                            let errRes: Response = {
                                ...new Response,
                                ok: false,
                                status: 500,
                            }
                            return errRes;
                        })
                }
            } else {
            }
            appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext.state.currentChat });
            setMessages(appStateContext.state.currentChat.messages)
            setProcessMessages(messageStatus.NotRunning)
        }
    }, [processMessages]);

    useEffect(() => {
        if (AUTH_ENABLED !== undefined) getUserInfoList();
    }, [AUTH_ENABLED]);

    useLayoutEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" })
    }, [showLoadingMessage, processMessages]);

    const onShowCitation = (citation: Citation) => {
        setActiveCitation(citation);
        setIsCitationPanelOpen(true);
    };

    const onViewSource = (citation: Citation) => {
        if (citation.url && !citation.url.includes("blob.core")) {
            window.open(citation.url, "_blank");
        }
    };

    const parseCitationFromMessage = (message: ChatMessage) => {
        if (message?.role && message?.role === "tool") {
            try {
                const toolMessage = JSON.parse(message.content) as ToolMessageContent;
                return toolMessage.citations;
            }
            catch {
                return [];
            }
        }
        return [];
    }

    const disabledButton = () => {
        return isLoading || (messages && messages.length === 0) || clearingChat || appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading
    }

    return (
        <div className={styles.container} role="main">
            {showAuthMessage ? ( //RENATO - Auth screen
                <Stack className={styles.chatEmptyState}>
                    <ShieldLockRegular className={styles.chatIcon} style={{ color: 'darkorange', height: "200px", width: "200px" }} />
                    <h1 className={styles.chatEmptyStateTitle}>Authentication Not Configured</h1>
                    <h2 className={styles.chatEmptyStateSubtitle}>
                        This app does not have authentication configured. Please add an identity provider by finding your app in the <a href="https://portal.azure.com/" target="_blank">Azure Portal</a>
                        and following <a href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization" target="_blank">these instructions</a>.
                    </h2>
                    <h2 className={styles.chatEmptyStateSubtitle} style={{ fontSize: "20px" }}><strong>Authentication configuration takes a few minutes to apply. </strong></h2>
                    <h2 className={styles.chatEmptyStateSubtitle} style={{ fontSize: "20px" }}><strong>If you deployed in the last 10 minutes, please wait and reload the page after 10 minutes.</strong></h2>
                </Stack>
            ) : (
                <Stack horizontal className={styles.chatRoot}>
                    <div className={styles.chatContainer}>
                        {(!messages || messages.length < 1) && id === null ? (
                            <Stack className={styles.chatEmptyState}>
                                <img
                                    src={ui?.chat_logo ? ui.chat_logo : Contoso}
                                    className={styles.chatIcon}
                                    aria-hidden="true"
                                />
                                <h1 className={styles.chatEmptyStateTitle}>{ui?.chat_title}</h1>
                                <h2 className={styles.chatEmptyStateSubtitle}>{ui?.chat_description}</h2>
                                <a className={styles.chatLink} target="_blank" href="https://documenter.getpostman.com/view/2466848/2sA2rGvKmZ">BitFractal API</a>
                                <a className={styles.chatLink} target="_blank" href="https://www.bitfractal.com/post/bitfractal-tts-languages">Supported Languages and Voices</a>
                                <a className={styles.chatLink} target="_blank" href="https://www.bitfractal.com/">BitFractal Website</a>
                                <a className={styles.chatLink} href="mailto:support@bitfractal.com">BitFractal Support</a>
                            </Stack>
                        ) : (
                            <div className={styles.chatMessageStream} style={{ marginBottom: isLoading ? "40px" : "0px" }} role="log">
                                {messages.map((answer, index) => (
                                    <>
                                        {answer.role === "user" ?
                                            (answer.type === "audio" ?
                                                (
                                                    <div className={styles.chatMessageUser} tabIndex={0}>
                                                        <audio className={styles.chatMessageUser} controls src={answer.audio?.src} />
                                                    </div>
                                                ) : (
                                                    <div className={styles.chatMessageUser} tabIndex={0}>
                                                        <div className={styles.chatMessageUserMessage}>{answer.content}</div>
                                                    </div>
                                                )) :
                                            (answer.role === "assistant" || answer.role === "intro" ?
                                                (answer.type === "audio" ? (
                                                    <div className={styles.chatMessageGpt}>
                                                        <audio className={styles.chatMessageUser} autoPlay controls src={answer.audio?.src} />
                                                    </div>) : (answer.type === "text" ?
                                                        (<div className={styles.chatMessageGpt}>
                                                            <Answer
                                                                answer={{
                                                                    answer: answer.content,
                                                                    citations: parseCitationFromMessage(messages[index - 1]),
                                                                    message_id: answer.id,
                                                                    feedback: answer.feedback
                                                                }}
                                                                onCitationClicked={c => onShowCitation(c)}
                                                            />
                                                        </div >) : (answer.type === "metadata" ? (
                                                            <div className={styles.chatMessageMetadata}>
                                                                <Stack horizontal className={styles.chatMessageMetadataContent}>
                                                                    <ErrorCircleRegular className={styles.errorIcon} style={{ color: "rgb(26, 55, 0)" }} />
                                                                    <span>Message Metadata</span>
                                                                </Stack>
                                                                <span className={styles.chatMessageErrorContent}>{answer.content}</span>
                                                            </div>) : (
                                                            <div className={styles.chatMessageAutoQM}>
                                                                <Stack horizontal className={styles.chatMessageAutoQMContent}>
                                                                    <ClipboardCheckmarkRegular className={styles.autoQMIcon} style={{ color: "green" }} />
                                                                    <span className={styles.chatMessageAutoQMTitle}>AutoQM Results</span>
                                                                </Stack>
                                                                <Stack>
                                                                    {answer.title && (<div className={styles.autoqmcard}>
                                                                        <span className={styles.autoqmheader}>{answer.title}</span>
                                                                    </div>)}
                                                                    {answer.issue && (<div className={styles.autoqmcard}>
                                                                        <span className={styles.autoqmheader}>Issue</span>
                                                                        <span className={styles.chatMessageAutoQMContent}>{answer.issue}</span>
                                                                    </div>)}
                                                                    {answer.resolution && (<div className={styles.autoqmcard}>
                                                                        <span className={styles.autoqmheader}>Resolution</span>
                                                                        <span className={styles.chatMessageAutoQMContent}>{answer.resolution}</span>
                                                                    </div>)}
                                                                    {answer.recap && (<div className={styles.autoqmcard}>
                                                                        <span className={styles.autoqmheader}>Recap</span>
                                                                        <span className={styles.chatMessageAutoQMContent}>{answer.recap}</span>
                                                                    </div>)}
                                                                    {answer.narrative && (<div className={styles.autoqmcard}>
                                                                        <span className={styles.autoqmheader}>Narrative</span>
                                                                        <span className={styles.chatMessageAutoQMContent}>{answer.narrative}</span>
                                                                    </div>)}
                                                                    <div className={styles.autoqmcard}>
                                                                        <span className={styles.autoqmheader}>AutoQM Score</span>
                                                                        <span className={styles.chatMessageAutoQMContent}>{answer.score}</span>
                                                                    </div>
                                                                    <div className={styles.autoqmcard}>
                                                                        <span className={styles.autoqmheader}>Autofilled QM Form</span>
                                                                        {answer.answers?.map((value) => (
                                                                            <>
                                                                                <Stack>
                                                                                    <span className={styles.chatMessageAutoQMQuestion}>{value.question}</span>
                                                                                    {value.answer.answer ? (<span className={styles.chatMessageAutoQMAnswer}>{value.answer.answer}</span>) : (<span className={styles.chatMessageAutoQMAnswer}>{value.answer}</span>)}
                                                                                    {<span className={styles.chatMessageAutoQMContent}><span style={{ fontWeight: "bold" }}>Choice: </span>{value.answer.choice} <span style={{ fontWeight: "bold" }}>Score: </span>{value.answer.score}</span>}
                                                                                </Stack>
                                                                            </>
                                                                        ))}

                                                                    </div>
                                                                </Stack>
                                                            </div>
                                                        )
                                                        )
                                                )) : answer.role === ERROR ? <div className={styles.chatMessageError}>
                                                    <Stack horizontal className={styles.chatMessageErrorContent}>
                                                        <ErrorCircleRegular className={styles.errorIcon} style={{ color: "rgba(182, 52, 67, 1)" }} />
                                                        <span>Error</span>
                                                    </Stack>
                                                    <span className={styles.chatMessageErrorContent}>{answer.content}</span>
                                                </div> : null)
                                        }
                                    </>
                                ))}
                                {showLoadingMessage && (
                                    <>
                                        <div className={styles.chatMessageGpt}>
                                            <div className={styles.dotbody}>
                                                <div className={styles.dot1}></div>
                                                <div className={styles.dot2}></div>
                                                <div className={styles.dot3}></div>
                                            </div>
                                            {/* <Answer
                                                answer={{
                                                    answer: "Generating answer...",
                                                    citations: []
                                                }}
                                                onCitationClicked={() => null}
                                            /> */}
                                        </div>
                                    </>
                                )}
                                <div ref={chatMessageStreamEnd} />
                            </div>
                        )}

                        <Stack horizontal className={styles.chatInput}>
                            {isLoading && (
                                <Stack
                                    horizontal
                                    className={styles.stopGeneratingContainer}
                                    role="button"
                                    aria-label="Stop generating"
                                    tabIndex={0}
                                    onClick={stopGenerating}
                                    onKeyDown={(e: { key: string; }) => e.key === "Enter" || e.key === " " ? stopGenerating() : null}
                                >
                                    <SquareRegular className={styles.stopGeneratingIcon} aria-hidden="true" />
                                    <span className={styles.stopGeneratingText} aria-hidden="true">Stop generating</span>
                                </Stack>
                            )}
                            <Stack>
                                {appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured && <CommandBarButton
                                    role="button"
                                    styles={{
                                        icon: {
                                            color: '#FFFFFF',
                                        },
                                        iconDisabled: {
                                            color: "#BDBDBD !important"
                                        },
                                        root: {
                                            color: '#FFFFFF',
                                            background: "radial-gradient(106.04% 106.06% at 100.1% 90.19%,#FD6262 33.63%,#e7b2b2 100%)"
                                        },
                                        rootDisabled: {
                                            background: "#F0F0F0"
                                        }
                                    }}
                                    className={styles.newChatIcon}
                                    iconProps={{ iconName: 'Add' }}
                                    onClick={newChat}
                                    disabled={disabledButton()}
                                    aria-label="start a new chat button"
                                />}
                                {id === null &&
                                    <CommandBarButton
                                        role="button"
                                        styles={{
                                            icon: {
                                                color: '#FFFFFF',
                                            },
                                            iconDisabled: {
                                                color: "#BDBDBD !important",
                                            },
                                            root: {
                                                color: '#FFFFFF',
                                                background: "radial-gradient(106.04% 106.06% at 100.1% 90.19%,#FD6262 33.63%,#e7b2b2 100%)",
                                            },
                                            rootDisabled: {
                                                background: "#F0F0F0"
                                            }
                                        }}
                                        className={appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured ? styles.clearChatBroom : styles.clearChatBroomNoCosmos}
                                        iconProps={{ iconName: 'EntitlementRedemption' }}
                                        onClick={() => autoQM(appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined)}//appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured ? clearChat : newChat}
                                        // disabled={disabledButton()}
                                        aria-label="clear chat button"
                                    />}
                                <AudioRecorder
                                    onRecordingComplete={(blob) => addAudioElement(blob, appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined)}
                                    audioTrackConstraints={{
                                        noiseSuppression: true,
                                        echoCancellation: true,
                                    }}
                                    onNotAllowedOrFound={(err) => console.table(err)}
                                    showVisualizer={true}
                                    //downloadOnSavePress
                                    downloadFileExtension="webm"
                                />
                                <Dialog
                                    hidden={hideErrorDialog}
                                    onDismiss={handleErrorDialogClose}
                                    dialogContentProps={errorDialogContentProps}
                                    modalProps={modalProps}
                                >
                                </Dialog>
                            </Stack>
                            <QuestionInput
                                clearOnSend
                                placeholder="Type a new question..."
                                disabled={isLoading}
                                onSend={(question, id) => {
                                    appStateContext?.state.isCosmosDBAvailable?.cosmosDB ? makeApiRequestWithCosmosDB(question, id) : makeApiRequestWithoutCosmosDB(question, id)
                                }}
                                conversationId={appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined}
                            />
                        </Stack>
                    </div>
                    {/* Citation Panel */}
                    {messages && messages.length > 0 && isCitationPanelOpen && activeCitation && (
                        <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Citations Panel">
                            <Stack aria-label="Citations Panel Header Container" horizontal className={styles.citationPanelHeaderContainer} horizontalAlign="space-between" verticalAlign="center">
                                <span aria-label="Citations" className={styles.citationPanelHeader}>Citations</span>
                                <IconButton iconProps={{ iconName: 'Cancel' }} aria-label="Close citations panel" onClick={() => setIsCitationPanelOpen(false)} />
                            </Stack>
                            <h5 className={styles.citationPanelTitle} tabIndex={0} title={activeCitation.url && !activeCitation.url.includes("blob.core") ? activeCitation.url : activeCitation.title ?? ""} onClick={() => onViewSource(activeCitation)}>{activeCitation.title}</h5>
                            <div tabIndex={0}>
                                <ReactMarkdown
                                    linkTarget="_blank"
                                    className={styles.citationPanelContent}
                                    children={DOMPurify.sanitize(activeCitation.content, { ALLOWED_TAGS: XSSAllowTags })}
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                />
                            </div>
                        </Stack.Item>
                    )}
                    {(appStateContext?.state.isChatHistoryOpen && appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured) && <ChatHistoryPanel />}
                </Stack>
            )
            }
        </div >
    );
};

export default Chat;
